import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';

export default function AdminLocations() {
  const { fetchWithAuth } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const loadLocations = useCallback(async () => {
    try {
      setError('');
      const res = await fetchWithAuth('/api/consignment-locations');
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load locations');
      setLocations(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const handleOpenCreate = () => {
    setEditing(null);
    setName('');
    setCode('');
    setOpen(true);
  };

  const handleOpenEdit = (loc) => {
    setEditing(loc);
    setName(loc.name || '');
    setCode(loc.code || '');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setName('');
    setCode('');
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }
    try {
      setError('');
      if (editing) {
        const res = await fetchWithAuth(`/api/consignment-locations/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmedName, code: code.trim() || null }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Update failed');
      } else {
        const res = await fetchWithAuth('/api/consignment-locations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmedName, code: code.trim() || null }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Create failed');
      }
      handleClose();
      loadLocations();
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  const handleDelete = async (loc) => {
    if (!window.confirm(`Delete location "${loc.name}"? Users assigned to it will be unassigned.`)) return;
    try {
      setError('');
      const res = await fetchWithAuth(`/api/consignment-locations/${loc.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Delete failed');
      loadLocations();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Consignment Locations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Outlets where the main branch dispatches parts. Agents receive goods at their assigned location.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenCreate} sx={{ mb: 2 }}>
          Add location
        </Button>

        {loading ? (
          <Typography color="text.secondary">Loading...</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      No locations yet. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((loc) => (
                    <TableRow key={loc.id}>
                      <TableCell>{loc.name}</TableCell>
                      <TableCell>{loc.code || '—'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenEdit(loc)} title="Edit">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(loc)} title="Delete" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>{editing ? 'Edit location' : 'New consignment location'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
              required
              placeholder="e.g. Branch A, Outlet North"
            />
            <TextField
              fullWidth
              label="Code (optional)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              margin="normal"
              placeholder="e.g. BR-A"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}
